import {
  APIFieldType,
  BubbleChartWidgetDatasource,
  DataSourceField,
  WidgetDatasource,
  WidgetDataSourceTypes,
  WidgetTypes,
} from '@ardoq/api-types';
import { SURVEY_VALID_INVALID_FIELD_NAME } from './types';

type WidgetTypeValidationAttributes = {
  datasource?: WidgetDatasource | BubbleChartWidgetDatasource;
  selectedFields: DataSourceField[];
  availableFieldsForDataSource?: DataSourceField[];
};

const VALID_LABEL_FIELDS_FOR_CATEGORICAL_CHART = new Set([
  APIFieldType.TEXT,
  APIFieldType.LIST,
  APIFieldType.CHECKBOX,
  APIFieldType.USER,
]);

type WidgetTypeValidationCriteria = (
  widgetData: WidgetTypeValidationAttributes
) => boolean;

const isReportDataSource: WidgetTypeValidationCriteria = ({ datasource }) =>
  datasource?.sourceType === WidgetDataSourceTypes.REPORT;
const hasLessThanTwoSelectedFields: WidgetTypeValidationCriteria = ({
  selectedFields,
}) => selectedFields.length < 2;

const isReferenceField = (field: DataSourceField): boolean =>
  Boolean(field.referenceTypeIncoming || field.referenceTypeOutgoing);

const isIdField = (field: DataSourceField): boolean =>
  field.name === '_id' || field.name === 'component-key';

export const isValidLabelFieldForCategoricalChart = (
  field: DataSourceField
): boolean =>
  VALID_LABEL_FIELDS_FOR_CATEGORICAL_CHART.has(field.type) &&
  !isReferenceField(field) &&
  // NOTE: For Stacked Bar, we only allow 'name' if custom gremlin result; for Pie/Bar,
  // anything goes. Reasoning: Name makes no sense, since names are (mostly) unique;
  // With the exception of custom, arbitrary  Gremlin results. Everything has them,
  // and we don't want to waste space in daily snapshots b/c of st. almost useless.
  // !(field.name === 'name' && !field.custom);
  !isIdField(field);

const hasNoReferenceFieldsSelected: WidgetTypeValidationCriteria = ({
  selectedFields,
}) => !selectedFields.some(isReferenceField);

const hasNoIdFieldSelected: WidgetTypeValidationCriteria = ({
  selectedFields,
}) => !selectedFields.some(isIdField);

const selectedFieldsIsValidForCategoricalChart: WidgetTypeValidationCriteria =
  ({ selectedFields }) => {
    // Cases: 1 label field, 1 number field, or both
    const hasLabel = selectedFields.some(isValidLabelFieldForCategoricalChart);
    const hasNumber = selectedFields.some(f => f.type === APIFieldType.NUMBER);
    return Boolean(
      selectedFields.length &&
      // We are less strict than validation b/c the user may add missing fields
      // later and because we ensure correct order automatically
      ((selectedFields.length === 1 && (hasLabel || hasNumber)) ||
        (selectedFields.length === 2 && hasLabel && hasNumber))
    );
  };

const hasSingleCategoryField: WidgetTypeValidationCriteria = ({
  selectedFields,
}) =>
  selectedFields.length === 1 &&
  isValidLabelFieldForCategoricalChart(selectedFields[0]);

// BE does not collect frequencies of the ever-present, mostly unique name field
const notArdoqNameField: WidgetTypeValidationCriteria = ({ selectedFields }) =>
  selectedFields.length === 1 &&
  !(selectedFields[0].name === 'name' && !selectedFields[0].custom);

const isReportDatasourceOrSurveyWithValidationFieldSelected: WidgetTypeValidationCriteria =
  widgetData =>
    isReportDataSource(widgetData) ||
    widgetData.selectedFields[0]?.name === SURVEY_VALID_INVALID_FIELD_NAME;

const isValidCategoricalChart: WidgetTypeValidationCriteria = widgetData =>
  hasNoReferenceFieldsSelected(widgetData) &&
  hasNoIdFieldSelected(widgetData) &&
  selectedFieldsIsValidForCategoricalChart(widgetData);

const widgetTypeValidationCriteria: Record<
  WidgetTypes,
  WidgetTypeValidationCriteria[]
> = {
  [WidgetTypes.TABLE]: [],
  [WidgetTypes.LINE_CHART]: [isReportDataSource, hasNoReferenceFieldsSelected],
  [WidgetTypes.NUMBER]: [
    hasLessThanTwoSelectedFields,
    hasNoReferenceFieldsSelected,
  ],
  [WidgetTypes.PIE_CHART]: [isValidCategoricalChart],
  [WidgetTypes.PERCENTAGE]: [isReportDataSource],
  [WidgetTypes.GAUGE]: [isReportDataSource],
  [WidgetTypes.BAR_CHART]: [isValidCategoricalChart],
  [WidgetTypes.STACKED_BAR_CHART]: [
    hasSingleCategoryField,
    isReportDatasourceOrSurveyWithValidationFieldSelected,
    notArdoqNameField,
  ],
  [WidgetTypes.BUBBLE_CHART]: [],
  [WidgetTypes.HEADER]: [],
  [WidgetTypes.NOTE]: [],
  [WidgetTypes.POWER_BI_IFRAME]: [],
};
export const isWidgetTypeValidForSelectedFieldsAndDatasource = ({
  widgetType,
  ...validationAttributes
}: WidgetTypeValidationAttributes & { widgetType: WidgetTypes }) =>
  widgetType !== WidgetTypes.HEADER &&
  widgetTypeValidationCriteria[widgetType].every(
    validationCriteriaForWidgetType =>
      validationCriteriaForWidgetType(validationAttributes)
  );

const MULTIPLE_SELECTED_FIELDS_POPOVER =
  'Number and stacked bar charts support at most one field';

const widgetTypeSpecificPopoverContent = {
  [WidgetTypes.LINE_CHART]: `Line chart can't be used with surveys or reference fields`,
  [WidgetTypes.NUMBER]: `Number chart can't be used with reference fields`,
  [WidgetTypes.PIE_CHART]:
    'Pie charts work with text, list, checkbox, or user types. You can also add a number type to show values.',
  [WidgetTypes.PERCENTAGE]:
    'Percentage charts work with text, list, checkbox, or user types. You can also add a number type to show values.',
  [WidgetTypes.GAUGE]:
    'Gauge charts work with text, list, checkbox, or user types. You can also add a number type to show values.',
  [WidgetTypes.BAR_CHART]:
    'Bar charts work with text, list, checkbox, or user types. You can also add a number type to show values.',
  [WidgetTypes.STACKED_BAR_CHART]:
    'Stacked bar chart can only be used with these report field types: text, list, checkbox, user and this survey field type: Show valid/invalid.',
  [WidgetTypes.BUBBLE_CHART]: undefined,
  [WidgetTypes.TABLE]: undefined, // table never needs a popover,
  [WidgetTypes.HEADER]: undefined, // header never needs a popover,
  [WidgetTypes.NOTE]: undefined, // note never needs a popover,
  [WidgetTypes.POWER_BI_IFRAME]: undefined, // iframe never needs a popover,
};

export const getIsWidgetTypeDisabledAndPopoverContent = ({
  widgetType,
  ...validationAttributes
}: WidgetTypeValidationAttributes & {
  widgetType: WidgetTypes;
}) => {
  const validationCriteriaThatFails = widgetTypeValidationCriteria[
    widgetType
  ].find(
    validationCriteriaForWidgetType =>
      !validationCriteriaForWidgetType(validationAttributes)
  );
  if (validationCriteriaThatFails) {
    return {
      isDisabled: true,
      popoverContent:
        validationCriteriaThatFails === hasLessThanTwoSelectedFields
          ? MULTIPLE_SELECTED_FIELDS_POPOVER
          : widgetTypeSpecificPopoverContent[widgetType],
    };
  }
  return { isDisabled: false };
};
